import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation }      from "@apollo/client";

import { PagenameContext } from "../../../Contexts/Pagename";

import useFetch from "./../../../Hooks/useFetch.js";

import Breadcrumbs from "../../../Components/Dashboard/Breadcrumbs";
import Button from "../../../Components/Common/Button/index.js";

import FormClientEditGeneralInfo from "./../../../Forms/client-edit-general-info/index.js";
import FormClientEditCompanyInfo from "./../../../Forms/client-edit-company-info/index.js";
import FormClientEditContactInfo from "./../../../Forms/client-edit-contact-info/index.js";
import FormClientEditLegalAddress, {
    FIELDS as LEGAL_ADDRESS_FIELDS,
} from "./../../../Forms/client-edit-legal-address/index.js";
import FormClientEditActualAddress, {
    FIELDS as ACTUAL_ADDRESS_FIELDS,
} from "./../../../Forms/client-edit-actual-address/index.js";
import FormClientEditCompanyContacts from "./../../../Forms/client-edit-company-contacts/index.js";
import FormClientEditBranchDetails from "./../../../Forms/client-edit-branch-details/index.js";
import FormClientEditBankDetails from "./../../../Forms/client-edit-bank-details/index.js";
import FormClientEditAdditionalInfo from "./../../../Forms/client-edit-additional-info/index.js";
import FormClientEditCurrencySettings from "./../../../Forms/client-edit-currency-settings/index.js";

import QueriesAdminClients from "./../../../Queries/AdminClients.js";
import QueriesDiscountGroups from "./../../../Queries/DiscountGroups.js";
import QueriesPaymentOfGood from "./../../../Queries/PaymentOfGood.js";
import QueriesDeliveryPlans from "./../../../Queries/DeliveryPlans.js";
import QueriesBusinessSpheres from "./../../../Queries/BusinessSpheres.js";
import QueriesAdminManagers from "./../../../Queries/AdminManagers.js";
import QueriesSuppliers from "./../../../Queries/Suppliers.js";

import styles from "./styles.module.css";


const ClientsAdd = (props) => {
    const pageContext = useContext(PagenameContext);

    const generalInfoRef = useRef(null);
    const companyInfoRef = useRef(null);
    const contactInfoRef = useRef(null);
    const legalAddressRef = useRef(null);
    const actualAddressRef = useRef(null);
    const companyContactsRef = useRef(null);
    const branchDetailsRef = useRef(null);
    const bankDetailsRef = useRef(null);
    const additionalInfoRef = useRef(null);
    const currencySettingsRef = useRef(null);

    const [isLoading, setIsLoading] = useState(false);

    /* --- */

    const navigate = useNavigate();

    /* --- */

    const [addClient] = useMutation(QueriesAdminClients.ADD_CLIENT);

    const fetchManagers = useFetch(QueriesAdminManagers.FETCH_ADMIN_MANAGERS, {});
    const fetchDiscountGroups = useFetch(QueriesDiscountGroups.FETCH_DISCOUNT_GROUPS);
    const fetchPaymentOfGood = useFetch(QueriesPaymentOfGood.FETCH_PAYMENT_OF_GOOD);
    const fetchDeliveryPlans = useFetch(QueriesDeliveryPlans.FETCH_DELIVERY_PLANS);
    const fetchBusinessSpheres = useFetch(QueriesBusinessSpheres.FETCH_BUSINESS_SPHERES);
    const fetchSuppliers = useFetch(QueriesSuppliers.SUPPLIERS);

    /* --- */

    const getOption = (opt) => ({
        name: opt?.id || null,
        label: opt?.value || opt?.fullName || "",
    });

    const getOptions = (data, fieldName) => {
        return (data?.[fieldName] || []).map((opt) => getOption(opt));
    };

    const getManagers = () => {
        const { loading, data } = fetchManagers;

        return {
            options: getOptions(data, "adminManagers"),
            isLoading: loading,
        };
    };

    const getDiscountGroups = () => {
        const { loading, data } = fetchDiscountGroups;

        return {
            options: getOptions(data, "discountGroups"),
            isLoading: loading,
        };
    };

    const getDeliveryPlan = () => {
        const { loading, data } = fetchDeliveryPlans;

        return {
            options: getOptions(data, "deliveryPlans"),
            isLoading: loading,
        };
    };

    const getBusinessSpheres = () => {
        const { loading, data } = fetchBusinessSpheres;

        return {
            options: getOptions(data, "businessSpheres"),
            isLoading: loading,
        };
    };

    const getDirectSuppliers = () => {
        const { loading, data } = fetchSuppliers;

        const options = (data?.edges || []).map((edge) => {
            const opt = edge?.node || {};

            return {
                name: opt?.id || null,
                label: opt?.name || "",
            };
        });

        return {
            options,
            isLoading: loading,
        };
    };

    const getPaymentOfGoods = () => {
        const { loading, data } = fetchPaymentOfGood;

        return {
            options: getOptions(data, "paymentOfGood"),
            isLoading: loading,
        };
    };

    /* --- */

    const navigateClients = () => {
        navigate("/app/clients");
    };

    /* --- */

    const getErrors = () => {
        const formsErrors = [
            generalInfoRef.current?.errors || {},
            companyInfoRef.current?.errors || {},
            contactInfoRef.current?.errors || {},
            legalAddressRef.current?.errors || {},
            actualAddressRef.current?.errors || {},
            additionalInfoRef.current?.errors || {},
            currencySettingsRef.current?.errors || {},
        ];

        const errors = [];

        for (let i = 0; i < formsErrors.length; i += 1) {
            const formErrors = formsErrors[i];

            for (const key in formErrors) {
                errors.push(formErrors[key]);
            }
        }

        return errors;
    };

    const getFilteredCompanyContacts = () => {
        const companyContacts = companyContactsRef.current.values.rows || [];
        const filtered = [];

        for (let i = 0; i < companyContacts.length; i += 1) {
            const contacts = companyContacts[i];

            if (contacts.isDeleted) {
                continue;
            }

            filtered.push({
                firstName: contacts.companyContactsFirstName,
                lastName: contacts.companyContactsLastName,
                phone: contacts.companyContactsMobilePhone,
                email: contacts.companyContactsEmail,
                isMain: i === 0,
            });
        }

        return filtered;
    };

    const getFilteredBranchDetails = () => {
        const branchDetails = branchDetailsRef.current.values.rows || [];
        const filtered = [];

        for (let i = 0; i < branchDetails.length; i += 1) {
            const branch = branchDetails[i];

            if (branch.isDeleted) {
                continue;
            }

            filtered.push({
                name: branch.branchDetailsBranchName,
                country: branch.branchDetailsCountry,
                deliveryPlanId: branch.branchDetailsDeliveryPlan,
                address: branch.branchDetailsAddress,
                comment: branch.branchDetailsCommentShippingAddress,
            });
        }

        return filtered;
    };

    const getFilteredBankDetails = () => {
        const bankDetails = bankDetailsRef.current.values.rows || [];
        const filtered = [];

        for (let i = 0; i < bankDetails.length; i += 1) {
            const bank = bankDetails[i];

            if (bank.isDeleted) {
                continue;
            }

            filtered.push({
                name: bank.bankDetailsBankName,
                swift: bank.bankDetailsSwift,
                number: bank.bankDetailsAccountNumber,
                address: bank.bankDetailsBankAddress,
            });
        }

        return filtered;
    };

    const getAddClientParams = () => {
        const generalInfo = generalInfoRef.current.values;
        const companyInfo = companyInfoRef.current.values;
        const contactInfo = contactInfoRef.current.values;
        const legalAddress = legalAddressRef.current.values;
        const actualAddress = actualAddressRef.current.values;
        const additionalInfo = additionalInfoRef.current.values;
        const currencySettings = currencySettingsRef.current.values;

        const params = {
            generalInfoCreditLimit: parseFloat(generalInfo.generalInfoCreditLimit || 0) || 0,
            generalInfoTermOfPayment: generalInfo.generalInfoTermOfPayment || "",
            generalInfoCreditLimitBalance: parseFloat(generalInfo.generalInfoCreditLimitBalance || 0) || 0,
            generalInfoUnpaidInvoices: parseInt(generalInfo.generalInfoUnpaidInvoices || 0) || 0,
            generalInfoDebt: parseFloat(generalInfo.generalInfoDebt || 0) || 0,
            generalInfoContractNumber: generalInfo.generalInfoContractNumber || "",
            generalInfoDateOfContract: generalInfo.generalInfoDateOfContract || "",
            generalInfoManager: parseInt(generalInfo.generalInfoManager || 0) || 0,
            generalInfoDiscountGroup: parseInt(generalInfo.generalInfoDiscountGroup || 0) || 0,
            generalInfoPaymentOfGoods: parseInt(generalInfo.generalInfoPaymentOfGoods || 0) || 0,
            generalInfoDeliveryPlan: parseInt(generalInfo.generalInfoDeliveryPlan || 0) || 0,
            companyInfoCompanyName: companyInfo.companyInfoCompanyName || "",
            companyInfoRegistrationName: companyInfo.companyInfoRegistrationName || "",
            companyInfoRegistrationNumber: companyInfo.companyInfoRegistrationNumber || "",
            companyInfoVATRegistrationNumber: companyInfo.companyInfoVATRegistrationNumber || "",
            companyInfoHomepage: companyInfo.companyInfoHomepage || "",
            contactInfoFirstName: contactInfo.contactInfoFirstName || "",
            contactInfoLastName: contactInfo.contactInfoLastName || "",
            contactInfoMobilePhone: contactInfo.contactInfoMobilePhone || "",
            contactInfoEmail: contactInfo.contactInfoEmail || "",
            contactInfoLandlinePhone: contactInfo.contactInfoLandlinePhone || "",
            contactInfoFax: contactInfo.contactInfoFax || "",
            contactInfoAccountingPhone: contactInfo.contactInfoAccountingPhone || "",
            contactInfoAccountingEmail: contactInfo.contactInfoAccountingEmail || "",
            legalAddressCountry: legalAddress.legalAddressCountry || "",
            legalAddressCity: legalAddress.legalAddressCity || "",
            legalAddressAddress: legalAddress.legalAddressAddress || "",
            legalAddressPostCode: legalAddress.legalAddressPostCode || "",
            actualAddressCountry: actualAddress.actualAddressCountry || "",
            actualAddressCity: actualAddress.actualAddressCity || "",
            actualAddressAddress: actualAddress.actualAddressAddress || "",
            actualAddressPostCode: actualAddress.actualAddressPostCode || "",
            actualAddressComment: actualAddress.actualAddressComment || "",
            additionalInfoLanguage: additionalInfo.additionalInfoLanguage || "",
            additionalInfoBusinessSphere: parseInt(additionalInfo.additionalInfoBusinessSphere || 0) || 0,
            additionalInfoComment: additionalInfo.additionalInfoComment || "",
            additionalInfoDirectSuppliers: [parseInt(additionalInfo.additionalInfoDirectSuppliers || 0)],
            additionalInfoLogoFile: additionalInfo.additionalInfoLogoFile || null,
            additionalInfoIsAPIAccess: additionalInfo.additionalInfoIsAPIAccess || false,
            additionalInfoAPIAccess: additionalInfo.additionalInfoAPIAccess || "",
            additionalInfoIsDiscounts: additionalInfo.additionalInfoIsDiscounts || false,
            additionalInfoIsNews: additionalInfo.additionalInfoIsNews || false,
            additionalInfoIsOnSale: additionalInfo.additionalInfoIsOnSale || false,
            additionalInfoIsTheNews: additionalInfo.additionalInfoIsTheNews || false,
            companyContacts: getFilteredCompanyContacts(),
            branchDetails: getFilteredBranchDetails(),
            bankDetails: getFilteredBankDetails(),
            currency: currencySettings.currency || "",
            showVAT: currencySettings.showVAT || false,
        };

        return params;
    };

    const validateAllForms = async () => {
        const forms = [
            generalInfoRef.current,
            companyInfoRef.current,
            contactInfoRef.current,
            legalAddressRef.current,
            actualAddressRef.current,
            additionalInfoRef.current,
            currencySettingsRef.current,
        ];

        for (let i = 0; i < forms.length; i += 1) {
            const form = forms[i];

            if (form?.validateForm) {
                await form.validateForm();
            }
        }
    };

    const onSave = async () => {
        setIsLoading(true);

        await validateAllForms();

        const formErrors = getErrors();

        if (formErrors.length) {
            setIsLoading(false);
            return;
        }

        const variables = getAddClientParams();

        let error = "";

        try {
            const res = await addClient({ variables });

            const err = res?.errors?.[0]?.message || "";

            if (err) {
                error = err;
            }
        } catch (err) {
            error = err?.message || "";
        }

        if (error) {
            alert(`Failed to add client: ${error}`);
        }

        setIsLoading(false);

        if (!error) {
            navigateClients()
        }
    };

    /* --- */

    useEffect(() => {
        if (props.name && pageContext.setPagename) {
            pageContext.setPagename(props.name);
        }
    }, []);

    /* --- */

    const renderControls = () => {
        const errors = getErrors();

        let errorList = null;

        if (errors.length) {
            errorList = errors.map((err, i) => (
                <div
                    key={`err-${i}`}
                    className={styles.error}
                >
                    {err}
                </div>
            ));
        }

        return (
            <div>
                <div className={styles.controls}>
                    <Button
                        isGrey
                        isDisabled={isLoading}
                        onClick={navigateClients}
                    >
                        Back
                    </Button>
                    <Button
                        onClick={onSave}
                        isDisabled={isLoading}
                        isBlue
                    >
                        {isLoading ? "Loading..." : "Save"}
                    </Button>
                </div>
                <div className={styles.errorList}>
                    {errorList}
                </div>
            </div>
        );
    };

    /* --- */

    const renderFormCurrencySettings = () => {
        return (
            <FormClientEditCurrencySettings
                formRef={currencySettingsRef}
                isEditable
            />
        );
    };

    const renderFormAdditionalInfo = () => {
        const businessSpheres = getBusinessSpheres();
        const directSuppliers = getDirectSuppliers();

        return (
            <FormClientEditAdditionalInfo
                formRef={additionalInfoRef}
                businessSpheres={{
                    options: businessSpheres.options,
                    isLoading: businessSpheres.isLoading,
                }}
                directSuppliers={{
                    options: directSuppliers.options,
                    isLoading: directSuppliers.isLoading,
                }}
                isEditable
            />
        );
    };

    const renderFormBankDetails = () => {
        return (
            <FormClientEditBankDetails
                formRef={bankDetailsRef}
                isEditable
            />
        );
    };

    const renderFormBranchDetails = () => {
        return (
            <FormClientEditBranchDetails
                formRef={branchDetailsRef}
                isEditable
            />
        );
    };

    const renderFormCompanyContacts = () => {
        return (
            <FormClientEditCompanyContacts
                formRef={companyContactsRef}
                isEditable
            />
        );
    };

    const renderFormActualAddress = () => {
        return (
            <FormClientEditActualAddress
                formRef={actualAddressRef}
                isEditable
            />
        );
    };

    const renderFormLegalAddress = () => {
        return (
            <FormClientEditLegalAddress
                formRef={legalAddressRef}
                onSameAsLegal={() => {
                    const { values } = legalAddressRef.current;

                    actualAddressRef.current.setValues({
                        [ACTUAL_ADDRESS_FIELDS.country]: values[LEGAL_ADDRESS_FIELDS.country],
                        [ACTUAL_ADDRESS_FIELDS.city]: values[LEGAL_ADDRESS_FIELDS.city],
                        [ACTUAL_ADDRESS_FIELDS.address]: values[LEGAL_ADDRESS_FIELDS.address],
                        [ACTUAL_ADDRESS_FIELDS.postCode]: values[LEGAL_ADDRESS_FIELDS.postCode],
                    });
                }}
                isEditable
            />
        );
    };

    const renderFormContactInfo = () => {
        return (
            <FormClientEditContactInfo
                formRef={contactInfoRef}
                isEditable
            />
        );
    };

    const renderFormCompanyInfo = () => {
        return (
            <FormClientEditCompanyInfo
                formRef={companyInfoRef}
                isEditable
            />
        );
    };

    const renderFormGeneralInfo = () => {
        const managers = getManagers();
        const discountGroups = getDiscountGroups();
        const deliveryPlan = getDeliveryPlan();
        const paymentOfGoods = getPaymentOfGoods();

        return (
            <FormClientEditGeneralInfo
                formRef={generalInfoRef}
                managers={{
                    options: managers.options,
                    isLoading: managers.isLoading,
                }}
                discountGroups={{
                    options: discountGroups.options,
                    isLoading: discountGroups.isLoading,
                }}
                deliveryPlan={{
                    options: deliveryPlan.options,
                    isLoading: deliveryPlan.isLoading,
                }}
                paymentOfGoods={{
                    options: paymentOfGoods.options,
                    isLoading: paymentOfGoods.isLoading,
                }}
                isEditable
            />
        );
    };

    /* --- */

    const renderContent = () => {
        return (
            <>
                {renderFormGeneralInfo()}
                <div className={styles.formsColumn}>
                    {renderFormCompanyInfo()}
                    {renderFormContactInfo()}
                </div>
                <div className={styles.formsColumn}>
                    {renderFormLegalAddress()}
                    {renderFormActualAddress()}
                </div>
                {renderFormCompanyContacts()}
                {renderFormBranchDetails()}
                {renderFormBankDetails()}
                {renderFormAdditionalInfo()}
                {renderFormCurrencySettings()}

                {renderControls()}
            </>
        );
    };

    /* --- */

    return (
        <section className="clientsAdd">
            <Breadcrumbs />
            {renderContent()}
        </section>
    );
};

ClientsAdd.defaultProps = {
    name: "",
};

export default ClientsAdd;
